import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Drawer,
  CircularProgress,
  IconButton,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip as ButtonToolTip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import CleaningServicesIcon from "@mui/icons-material/CleaningServicesTwoTone";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  PieController,
  DoughnutController,
} from "chart.js";

import ConfirmationDialog from "../components/ConfirmationDialog";
import { useMessage } from "../contexts/MessageContext";
import { getAllBusinesses } from "../services/adminService";
import {
  getDetailedFeedbackStats,
  deleteFeedbackForBusiness,
} from "../services/feedbackService";
import { useAuth } from "../contexts/AuthContext";

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  PieController,
  DoughnutController
);

const FeedbackReports = () => {
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const { user } = useAuth();
  const { showMessage } = useMessage();
  const [startDate, setStartDate] = useState(""); // Default to empty
  const [endDate, setEndDate] = useState(""); // Default to empty
  const [compareKey1, setCompareKey1] = useState("");
  const [compareKey2, setCompareKey2] = useState("");

  const validKeys = [
    "moodCheck",
    "energyLevel",
    "workEnvironment",
    "recognition",
    "workloadSatisfaction",
    "engagement",
    "teamDynamics",
    "wellnessPoll",
  ];

  // Load all businesses
  const loadBusinesses = useCallback(async () => {
    if (!user) return;

    setLoadingBusinesses(true);

    try {
      if (user.role === "admin") {
        const data = await getAllBusinesses();
        setBusinesses(data);
      } else if (user.role === "businessOwner") {
        const stats = await getDetailedFeedbackStats(
          user.id,
          startDate ? new Date(startDate).toISOString() : undefined,
          endDate ? new Date(endDate).toISOString() : undefined,
          compareKey1,
          compareKey2
        );
        setFeedbackStats(stats);

        setSelectedBusiness({
          name: user.name,
          email: user.email,
          phone: user.phone,
          id: user.id,
        });
      }
    } catch (error) {
      showMessage("Failed to fetch businesses.", "error");
    } finally {
      setLoadingBusinesses(false);
    }
  }, [user, showMessage, startDate, endDate, compareKey1, compareKey2]);

  // Load feedback stats
  const loadFeedbackStats = useCallback(
    async (businessId) => {
      if (!user) return;
      setLoadingStats(true);

      try {
        const { totalFeedbacks, results: stats } =
          await getDetailedFeedbackStats(
            businessId,
            startDate ? new Date(startDate).toISOString() : undefined,
            endDate ? new Date(endDate).toISOString() : undefined,
            compareKey1 || "",
            compareKey2 || ""
          );

        if (!stats || typeof stats !== "object") {
          throw new Error("Invalid response from the server");
        }

        setTotalFeedbacks(totalFeedbacks);
        setFeedbackStats(stats);

        // Only admins update `selectedBusiness`
        if (user.role === "admin") {
          const selected = businesses.find((biz) => biz.id === businessId);
          if (selected) {
            setSelectedBusiness(selected);
          }
        }
      } catch (error) {
        showMessage("Failed to load feedback stats.", "error");
      } finally {
        setLoadingStats(false);
        setDrawerOpen(false);
      }
    },
    [
      user,
      startDate,
      endDate,
      compareKey1,
      compareKey2,
      businesses,
      showMessage,
    ]
  );

  // Handle Reset Feedback
  const handleResetFeedback = async () => {
    if (!selectedBusiness?.id) return;

    try {
      await deleteFeedbackForBusiness(selectedBusiness.id);
      showMessage(
        "Feedback successfully reset for the selected business.",
        "success"
      );
      setFeedbackStats(null); // Clear feedback stats after deletion
      loadFeedbackStats(selectedBusiness.id); // Reload stats
    } catch (error) {
      showMessage("Failed to reset feedback. Please try again.", "error");
    }
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const handleCompareKey1Change = (e) => {
    setCompareKey1(e.target.value);
  };

  const handleCompareKey2Change = (e) => {
    setCompareKey2(e.target.value);
  };

  const applyDateFilter = useCallback(() => {
    if (selectedBusiness) {
      loadFeedbackStats(selectedBusiness.id);
    }
  }, [selectedBusiness, loadFeedbackStats]);

  useEffect(() => {
    if (selectedBusiness) {
      applyDateFilter();
    }
  }, [
    startDate,
    endDate,
    compareKey1,
    compareKey2,
    applyDateFilter,
    selectedBusiness,
  ]);

  // Fetch businesses on component mount
  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  const generatePieChartData = (data, label) => {
    const validData = data.filter(
      (item) => item.percentage !== null && item.percentage !== undefined
    );

    return {
      labels: validData.map((item) => `${item.category} (${item.percentage}%)`),
      datasets: [
        {
          label: `${label} Distribution`,
          data: validData.map((item) => item.percentage),
          backgroundColor: [
            "#2E86AB",
            "#A23E48",
            "#F18F01",
            "#C6DABF",
            "#5E2C83",
            "#F2B705",
          ],
          hoverOffset: 4,
        },
      ],
    };
  };
  const readableCategoryNames = {
    moodCheck: { title: "Mood Check", subtext: "Overall emotional state." },
    energyLevel: {
      title: "Energy Level",
      subtext: "Team's physical & mental energy.",
    },
    wellnessPoll: { title: "Wellness Poll", subtext: "Employee well-being." },
    workEnvironment: {
      title: "Work Environment",
      subtext: "Workplace productivity conditions.",
    },
    recognition: {
      title: "Recognition",
      subtext: "Colleagues recognized for contributions.",
    },
    workloadSatisfaction: {
      title: "Workload Satisfaction",
      subtext: "Workload balance assessment.",
    },
    engagement: {
      title: "Engagement",
      subtext: "Commitment & involvement level.",
    },
    teamDynamics: {
      title: "Team Dynamics Insight",
      subtext: "Collaboration & support level.",
    },
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* Drawer Button */}
      {user?.role === "admin" && !drawerOpen && (
        <IconButton
          color="primary"
          onClick={() => setDrawerOpen(true)}
          sx={{
            position: "fixed",
            top: { xs: 65, md: 80 },
            left: { xs: 16, md: 20 },
            zIndex: 2000,
            backgroundColor: (theme) => theme.palette.accent.main,
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Drawer for businesses */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "80%", md: 300 },
            boxSizing: "border-box",
            backgroundColor: "#fafafa",
            top: 60,
          },
        }}
      >
        <Box sx={{ padding: "1rem" }}>
          {/* Drawer Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Select a Business
            </Typography>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{ padding: "4px" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ marginBottom: "0.25rem" }} />
          {loadingBusinesses ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {businesses.map((business) => (
                <React.Fragment key={business.id}>
                  <ListItem
                    button
                    onClick={() => loadFeedbackStats(business.id)}
                    selected={selectedBusiness?.id === business.id}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        selectedBusiness?.id === business.id
                          ? "#e0f7fa"
                          : "inherit",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                    }}
                  >
                    <ListItemText
                      primary={business.name}
                      secondary={business.email}
                      primaryTypographyProps={{ fontWeight: "bold" }}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          padding: { xs: "1rem", md: "2rem" },
          mt: { xs: 4, md: 0 },
        }}
      >
        {loadingStats ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : feedbackStats ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: { xs: "center", sm: "space-between" },
                alignItems: "center",
                gap: { xs: 2, sm: 0 }, // Add spacing between elements for smaller screens
                marginBottom: "1rem",
                textAlign: { xs: "center", sm: "left" }, // Center text on small screens
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  marginBottom: { xs: "0.5rem", sm: 0 }, // Add margin for better spacing on small screens
                }}
              >
                {selectedBusiness?.name}'s Feedback Stats
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "primary.main",
                  fontWeight: "bold",
                  border: "1px solid",
                  borderColor: "primary.main",
                  padding: "4px 16px",
                  borderRadius: "4px",
                }}
              >
                Total Sessions: {totalFeedbacks}
              </Typography>

              {user?.role === "admin" && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon fontSize="small"/>}
                  onClick={() => setConfirmationOpen(true)}
                  sx={{
                    width: { xs: "100%", sm: "auto" }, // Make button full-width on small screens
                    textAlign: "center",
                  }}
                >
                  Delete Feedbacks
                </Button>
              )}
            </Box>

            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              Analyze feedback trends and stats through percentage-based graphs.
            </Typography>

            <Divider sx={{ marginBottom: "1rem" }} />
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                mb: 3,
                gap: 2,
              }}
            >
              {/* Date-Time Filters */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: 2,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <TextField
                  label="Start Date & Time"
                  type="datetime-local"
                  size="small"
                  value={startDate}
                  onChange={handleStartDateChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End Date & Time"
                  type="datetime-local"
                  size="small"
                  value={endDate}
                  onChange={handleEndDateChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                {(startDate || endDate || compareKey1 || compareKey2) && (
                  <ButtonToolTip title="Clear Dates & Keys Filters">
                    <Button
                      variant="contained"
                      color="accent"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                        setCompareKey1("");
                        setCompareKey2("");
                        applyDateFilter();
                      }}
                      startIcon={<CleaningServicesIcon fontSize="small" />}
                      sx={{
                        borderRadius: "8px",
                        padding: "4px 10px",
                        minWidth: "100px",
                        textTransform: "capitalize",
                        fontSize: "0.65rem",
                      }}
                    >
                      Clear
                    </Button>
                  </ButtonToolTip>
                )}
              </Box>

              {/* Comparison Filters */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: 2,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel>Compare Key 1</InputLabel>
                  <Select
                    value={compareKey1}
                    label="Compare Key 1"
                    onChange={handleCompareKey1Change}
                  >
                    <MenuItem value="">Select Key</MenuItem>{" "}
                    {/* Option to remove the filter */}
                    {validKeys.map((key) => (
                      <MenuItem key={key} value={key}>
                        {readableCategoryNames[key]?.title || key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel>Compare Key 2</InputLabel>
                  <Select
                    value={compareKey2}
                    label="Compare Key 2"
                    onChange={handleCompareKey2Change}
                  >
                    <MenuItem value="">Select Key</MenuItem>{" "}
                    {/* Option to remove the filter */}
                    {validKeys.map((key) => (
                      <MenuItem key={key} value={key}>
                        {readableCategoryNames[key]?.title || key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Grid container spacing={4}>
              {Object.entries(feedbackStats)
                .filter(([_, value]) => value.length > 0) // Exclude empty datasets
                .map(([key, value]) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    key={key}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <Box sx={{ maxWidth: 300 }}>
                      {" "}
                      {/* Limits chart size */}
                      <Typography
                        variant="h6"
                        sx={{ textAlign: "center", marginBottom: "0.5rem" }}
                      >
                        {readableCategoryNames[key]?.title || key}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ textAlign: "center", marginBottom: "1rem" }}
                      >
                        {readableCategoryNames[key]?.subtext || ""}
                      </Typography>
                      <Pie
                        data={generatePieChartData(
                          value,
                          readableCategoryNames[key]?.title || key
                        )}
                      />
                    </Box>
                  </Grid>
                ))}
            </Grid>
          </Box>
        ) : (
          <Typography
            variant="h6"
            sx={{ textAlign: "center", marginTop: "2rem" }}
          >
            {user?.role === "admin"
              ? businesses.length > 0
                ? "Select a business from the menu to view its feedback stats."
                : "No businesses found. Please add a business to view its feedback stats."
              : "No feedback stats available for your business."}
          </Typography>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={() => {
          handleResetFeedback();
          setConfirmationOpen(false);
        }}
        title="Reset Feedback"
        message={`Are you sure you want to reset all feedback for ${selectedBusiness?.name}? This action can't be undone.`}
        confirmButtonText="Reset"
      />
    </Box>
  );
};

export default FeedbackReports;
